import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trees, Pencil, Trash2, MapPin } from "lucide-react";
import { BackLink, DetailHeader, Panel, InfoGrid, ActionButton, AddButton, Empty, ConfirmDelete } from "@/components/Bits";
import { FazendaFormDialog } from "@/components/FazendaFormDialog";
import { EquipamentoFormDialog } from "@/components/EquipamentoFormDialog";
import { EquipTree } from "@/components/EquipTree";
import { OSHistory } from "@/components/OSHistory";
import { DocumentosPanel } from "@/components/DocumentosPanel";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

export default function FazendaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: f, reload } = useFetch(`/fazendas/${id}`);
  const { data: eqs, reload: reloadEq } = useFetch("/equipamentos", { fazenda_id: id });
  const [edit, setEdit] = useState(false);
  const [newEq, setNewEq] = useState(false);
  const [del, setDel] = useState(false);
  if (!f) return null;
  const isAdmin = user.role === "admin";
  const hasGeo = f.latitude != null && f.longitude != null;

  const remove = async () => {
    try { await api.delete(`/fazendas/${id}`); toast.success("Fazenda excluída"); navigate("/fazendas"); }
    catch (e) { toast.error(errMsg(e)); setDel(false); }
  };

  return (
    <div data-testid="fazenda-detail-page">
      <BackLink to="/fazendas" label="Fazendas" />
      <DetailHeader eyebrow="Fazenda" title={f.nome} subtitle={[f.municipio, f.estado].filter(Boolean).join(" / ")} icon={Trees} actions={<>
        {hasGeo && <a href={`https://www.google.com/maps?q=${f.latitude},${f.longitude}`} target="_blank" rel="noreferrer" data-testid="fazenda-map-link"
          className="h-12 px-4 rounded-xl font-semibold flex items-center gap-2 bg-[#C86D51] text-white hover:bg-[#B05A40]"><MapPin className="h-5 w-5" /> Ver no mapa</a>}
        {user.role !== "tecnico" && <ActionButton icon={Pencil} onClick={() => setEdit(true)} data-testid="fazenda-edit-button">Editar</ActionButton>}
        {isAdmin && <ActionButton icon={Trash2} variant="danger" onClick={() => setDel(true)} data-testid="fazenda-delete-button">Excluir</ActionButton>}
      </>} />
      <div className="space-y-5">
        <Panel title="Dados da fazenda" testId="fazenda-info">
          <InfoGrid items={[
            ["Cliente", f.cliente && (isAdmin ? <Link className="text-[#1E4D2B] font-semibold hover:underline" to={`/clientes/${f.cliente.cliente_id}`}>{f.cliente.nome}</Link> : f.cliente.nome)],
            ["Município / UF", [f.municipio, f.estado].filter(Boolean).join(" / ")], ["Endereço", f.endereco],
            ["Coordenadas", hasGeo ? `${f.latitude}, ${f.longitude}` : null], ["Responsável", f.responsavel], ["Telefone", f.telefone], ["Observações", f.observacoes]]} />
        </Panel>
        <Panel title={`Máquinas e equipamentos (${eqs?.length ?? 0})`} testId="fazenda-equipamentos"
          action={<AddButton small onClick={() => setNewEq(true)} data-testid="fazenda-add-equip">Equipamento</AddButton>}>
          {eqs?.length === 0 ? <Empty>Nenhum equipamento cadastrado.</Empty> : <EquipTree items={eqs || []} />}
        </Panel>
        <OSHistory filter={{ fazenda_id: id }} newQuery={{ cliente_id: f.cliente_id, fazenda_id: id }} />
        {isAdmin && <DocumentosPanel filter={{ fazenda_id: id }} fixed={{ cliente_id: f.cliente_id, fazenda_id: id }} />}
      </div>
      <FazendaFormDialog open={edit} onOpenChange={setEdit} editing={f} onSaved={reload} />
      <EquipamentoFormDialog open={newEq} onOpenChange={setNewEq} fazendaId={id} onSaved={reloadEq} />
      <ConfirmDelete open={del} onOpenChange={setDel} title="Excluir fazenda?" description="Só é possível excluir fazendas sem equipamentos e sem OS." onConfirm={remove} />
    </div>
  );
}
